import ResponseUtils from '../utils/ResponseUtils';
import Alert from './Alert';
import DnerhsApi from '../api/DnerhsApi';

/**
 * Manejador centralizado de errores para la aplicación
 */
class ErrorHandler {

  /**
   * Verifica si el servidor está realmente disponible
   * @returns {Promise<boolean>} True si el servidor responde
   */
  static async checkServerConnectivity() {
    try {
      // Intentar hacer una petición simple al servidor
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/health`, {
        method: 'GET',
        timeout: 5000,
        signal: AbortSignal.timeout(5000)
      });
      return response.ok;
    } catch (error) {
      console.warn("Server connectivity check failed:", error);
      return false;
    }
  }

  /**
   * Verifica la conectividad a internet
   * @returns {Promise<boolean>} True si hay conexión a internet
   */
  static async checkInternetConnectivity() {
    try {
      // Intentar conectar a un servicio externo confiable
      const response = await fetch('https://www.google.com/favicon.ico', {
        method: 'HEAD',
        mode: 'no-cors',
        timeout: 3000,
        signal: AbortSignal.timeout(3000)
      });
      return true;
    } catch (error) {
      console.warn("Internet connectivity check failed:", error);
      return false;
    }
  }
  
  /**
   * Maneja errores específicos del módulo de responsables
   * @param {Error} error - Error capturado
   * @param {string} context - Contexto donde ocurrió el error
   */
  static handleResponsableError(error, context = 'Responsable') {
    console.group(`🚨 Error en ${context}`);
    console.error("Error completo:", error);
    
    let message = "";
    let title = "Error";
    
    if (error.response) {
      const { status, data } = error.response;
      console.error("Response status:", status);
      console.error("Response data:", data);
      
      switch (status) {
        case 400:
          title = "Datos Incorrectos";
          if (data.message && data.message.includes('cedula')) {
            message = "La cédula de identidad ya está registrada o es inválida.";
          } else if (data.message && data.message.includes('email')) {
            message = "El email ya está registrado o tiene un formato inválido.";
          } else {
            message = ResponseUtils.getMessageError(error.response);
          }
          break;
          
        case 409:
          title = "Registro Duplicado";
          message = "Ya existe un responsable registrado con estos datos. Verifique la cédula de identidad y el email.";
          break;
          
        case 422:
          title = "Error de Validación";
          message = "Los datos enviados no cumplen con los requisitos:\n";
          if (data.errors) {
            data.errors.forEach(err => {
              message += `• ${err}\n`;
            });
          } else {
            message += ResponseUtils.getMessageError(error.response);
          }
          break;
          
        case 500:
          title = "Error del Servidor";

          // Detectar errores específicos de email
          if (data.message && data.message.includes("Mail server connection failed")) {
            title = "Error de Configuración de Email";
            message = "El responsable se creó correctamente, pero no se pudo enviar el email de notificación debido a un problema de configuración del servidor de correo. El administrador ha sido notificado.";
          }
          // Detectar otros errores de email
          else if (data.message && (data.message.includes("MailSendException") || data.message.includes("SMTP"))) {
            title = "Error de Email";
            message = "El responsable se creó correctamente, pero hubo un problema al enviar el email de notificación. Por favor, contacte al administrador.";
          }
          // Intentar extraer información útil del error sin exponer detalles técnicos
          else if (data.message && data.message !== "Internal Server Error") {
            // Filtrar mensajes técnicos comunes
            const technicalTerms = ['SQLException', 'NullPointerException', 'ClassNotFoundException', 'IOException'];
            const isTechnical = technicalTerms.some(term => data.message.includes(term));

            if (isTechnical) {
              message = "Error interno del servidor. El equipo técnico ha sido notificado. Por favor, intente más tarde.";
            } else {
              message = `Error del servidor: ${data.message}`;
            }
          } else if (data.error) {
            // Log completo para desarrolladores
            console.error("Detalles del error 500:", data.error);
            message = "Error interno del servidor. Revise la consola para más detalles técnicos.";
          } else {
            message = "Error interno del servidor. El equipo técnico ha sido notificado. Por favor, intente más tarde.";
          }
          break;
          
        default:
          message = ResponseUtils.getMessageError(error.response);
          break;
      }
    } else if (error.request) {
      // Error de red - usar detección inteligente
      const networkError = this.detectNetworkErrorType(error);
      title = networkError.title;
      message = networkError.message;
      console.error("Request sin respuesta:", error.request);
      console.error("Tipo de error de red:", networkError.type);
    } else {
      title = "Error de Configuración";
      message = `Error en la configuración: ${error.message}`;
      console.error("Error de configuración:", error.message);
    }
    
    console.groupEnd();
    
    Alert.show({
      message: `${title}: ${message}`,
      type: "error"
    });
  }
  
  /**
   * Maneja errores de validación de documentos
   * @param {string} documentType - Tipo de documento
   */
  static handleDocumentError(documentType = 'documento') {
    Alert.show({
      message: `El ${documentType} es requerido. Por favor, suba el archivo antes de continuar.`,
      type: "warning"
    });
  }
  
  /**
   * Maneja errores de validación de campos
   * @param {Object} errors - Objeto con errores de validación
   */
  static handleValidationErrors(errors) {
    let message = "Por favor, corrija los siguientes errores:\n";
    Object.keys(errors).forEach(field => {
      message += `• ${errors[field].message}\n`;
    });
    
    Alert.show({
      message,
      type: "warning"
    });
  }
  
  /**
   * Detecta el tipo específico de error de red
   * @param {Error} error - Error de red
   * @returns {Object} Objeto con tipo y mensaje específico
   */
  static detectNetworkErrorType(error) {
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

    // Intentar determinar el tipo específico de error
    if (error.message === "Network Error") {
      // Verificar si es un problema de CORS
      if (error.config && error.config.url && !error.config.url.startsWith(baseURL)) {
        return {
          type: "CORS",
          title: "Error de CORS",
          message: "Problema de configuración de CORS. El servidor no permite peticiones desde este dominio."
        };
      }

      // Verificar si es timeout
      if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
        return {
          type: "TIMEOUT",
          title: "Tiempo de Espera Agotado",
          message: "La conexión tardó demasiado en responder. El servidor puede estar sobrecargado."
        };
      }

      // Error genérico de red - probablemente servidor caído
      return {
        type: "CONNECTION_REFUSED",
        title: "Servidor No Disponible",
        message: "No se puede conectar al servidor. Posibles causas:\n" +
                "• El servidor backend no está funcionando\n" +
                "• El puerto 8080 está cerrado o bloqueado\n" +
                "• Problemas de red local"
      };
    }

    // Errores específicos por código
    if (error.code === "ECONNREFUSED") {
      return {
        type: "CONNECTION_REFUSED",
        title: "Conexión Rechazada",
        message: "El servidor rechazó la conexión. Verifique que el backend esté ejecutándose en el puerto correcto."
      };
    }

    if (error.code === "ENOTFOUND") {
      return {
        type: "DNS_ERROR",
        title: "Servidor No Encontrado",
        message: "No se puede resolver la dirección del servidor. Verifique la URL de configuración."
      };
    }

    if (error.code === "ECONNABORTED") {
      return {
        type: "TIMEOUT",
        title: "Conexión Interrumpida",
        message: "La conexión fue interrumpida. Intente nuevamente."
      };
    }

    if (error.message && error.message.includes("SSL")) {
      return {
        type: "SSL_ERROR",
        title: "Error de Certificado SSL",
        message: "Problema con el certificado de seguridad del servidor."
      };
    }

    // Error de red desconocido
    return {
      type: "UNKNOWN_NETWORK",
      title: "Error de Red",
      message: `Error de conexión: ${error.message || 'Error desconocido'}`
    };
  }

  /**
   * Maneja errores de red específicos con detección inteligente y verificación en tiempo real
   * @param {Error} error - Error de red
   */
  static async handleNetworkError(error) {
    const errorInfo = this.detectNetworkErrorType(error);

    console.group(`🌐 ${errorInfo.title}`);
    console.error("Tipo de error:", errorInfo.type);
    console.error("Error completo:", error);
    console.error("URL objetivo:", error.config?.url);
    console.error("Método:", error.config?.method);

    // Verificar conectividad en tiempo real para dar un diagnóstico más preciso
    if (errorInfo.type === "CONNECTION_REFUSED") {
      console.log("🔍 Verificando conectividad...");

      const [hasInternet, serverAvailable] = await Promise.all([
        this.checkInternetConnectivity(),
        this.checkServerConnectivity()
      ]);

      console.log("Internet disponible:", hasInternet);
      console.log("Servidor disponible:", serverAvailable);

      let detailedMessage = "";

      if (!hasInternet) {
        detailedMessage = "❌ Sin conexión a internet:\n" +
                         "• Verifique su conexión WiFi o datos móviles\n" +
                         "• Compruebe que no esté en modo avión\n" +
                         "• Reinicie su router si es necesario";
      } else if (!serverAvailable) {
        detailedMessage = "❌ Servidor backend no disponible:\n" +
                         "• El servidor no está ejecutándose\n" +
                         "• Verifique que esté corriendo en el puerto 8080\n" +
                         "• Contacte al administrador del sistema\n" +
                         "• Comando para iniciar: 'mvn spring-boot:run'";
      } else {
        detailedMessage = "⚠️ Error de conexión intermitente:\n" +
                         "• El servidor responde pero hubo un problema temporal\n" +
                         "• Intente nuevamente en unos segundos\n" +
                         "• Puede ser un problema de firewall o proxy";
      }

      console.groupEnd();

      Alert.show({
        message: `${errorInfo.title}: ${detailedMessage}`,
        type: "error"
      });

      return;
    }

    console.groupEnd();

    Alert.show({
      message: `${errorInfo.title}: ${errorInfo.message}`,
      type: "error"
    });
  }
  
  /**
   * Maneja errores de autenticación
   * @param {Error} error - Error de autenticación
   */
  static handleAuthError(error) {
    Alert.show({
      message: "Su sesión ha expirado. Por favor, inicie sesión nuevamente.",
      type: "warning"
    });
    
    // Redirigir al login después de un breve delay
    setTimeout(() => {
      window.location.href = '/login';
    }, 2000);
  }
  
  /**
   * Maneja errores genéricos con contexto
   * @param {Error} error - Error capturado
   * @param {string} context - Contexto donde ocurrió
   * @param {string} action - Acción que se estaba realizando
   */
  static handleGenericError(error, context = 'Aplicación', action = 'procesar la solicitud') {
    console.group(`🚨 Error en ${context}`);
    console.error(`Error al ${action}:`, error);
    console.groupEnd();
    
    let message = `Error al ${action}. `;
    
    if (error.response) {
      message += ResponseUtils.getMessageError(error.response);
    } else if (error.request) {
      message += "No se pudo conectar al servidor.";
    } else {
      message += error.message || "Error desconocido.";
    }
    
    Alert.show({
      message,
      type: "error"
    });
  }
}

export default ErrorHandler;
